/**
 * AI/ML Models Configuration Page
 * 
 * Centralized page for AI/ML model management:
 * - Model Selection & Configuration
 * - ML Training from Backtests
 * - Strategy Optimization
 * - Retrain Models Section
 * - Model Performance Metrics
 * - Learning System Settings
 * - Real Data Collection & Training (NEW)
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  Brain, Settings, Zap, RefreshCw, BarChart3, TrendingUp, 
  Activity, Database, Cpu, CheckCircle, AlertTriangle, Play,
  Pause, RotateCcw, Layers, Target, Clock, Info, Sparkles,
  GitBranch, LineChart, Award
} from 'lucide-react';
import DataCollectionDashboard from './DataCollectionDashboard';
import OTCTuningPanel from './OTCTuningPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available Models/Strategies
const AVAILABLE_MODELS = [
  { id: 'iq720_ensemble', name: 'IQ-720 Ensemble (Advanced)', category: 'AI Ensemble', accuracy: 82 },
  { id: 'enhanced_rsi_bb_volume', name: 'Enhanced RSI + BB + Volume', category: 'Technical', accuracy: 78 },
  { id: 'macd_crossover', name: 'MACD Crossover', category: 'Momentum', accuracy: 72 },
  { id: 'ema_crossover', name: 'EMA Crossover (7/21)', category: 'Trend', accuracy: 70 },
  { id: 'rsi_reversal', name: 'RSI Reversal', category: 'Oscillator', accuracy: 74 },
  { id: 'bollinger_squeeze', name: 'Bollinger Squeeze', category: 'Volatility', accuracy: 71 },
  { id: 'stoch_rsi', name: 'Stochastic RSI', category: 'Momentum', accuracy: 73 },
  { id: 'supertrend', name: 'SuperTrend', category: 'Trend', accuracy: 75 },
  { id: 'ichimoku', name: 'Ichimoku Cloud', category: 'Trend', accuracy: 69 },
  { id: 'support_resistance', name: 'Support/Resistance', category: 'Pattern', accuracy: 76 },
  { id: 'candlestick_patterns', name: 'Candlestick Patterns', category: 'Pattern', accuracy: 68 }
];

// ML Model Types
const ML_MODEL_TYPES = [
  { id: 'random_forest', name: 'Random Forest', description: 'Pattern classification with decision trees', icon: '🌲' },
  { id: 'gradient_boosting', name: 'Gradient Boosting', description: 'Sequential learning for accuracy', icon: '📈' },
  { id: 'ensemble', name: 'Ensemble', description: 'Combined predictions from multiple models', icon: '🎯' }
];

const AIMLModelsPage = () => {
  // Model Configuration State
  const [modelConfig, setModelConfig] = useState({
    primary_model: 'enhanced_rsi_bb_volume',
    secondary_model: 'support_resistance',
    use_ensemble: true,
    ensemble_method: 'weighted_average', // 'majority_vote', 'weighted_average', 'stacking'
    min_model_agreement: 2,
    confidence_threshold: 75
  });
  
  // AI Learning System State
  const [learningConfig, setLearningConfig] = useState({
    enabled: true,
    learning_rate: 0.01,
    adaptation_speed: 'medium', // 'slow', 'medium', 'fast'
    use_market_regime: true,
    use_volatility_filter: true,
    lookback_periods: 100,
    min_samples_for_update: 50
  });
  
  // Adaptive Strategy State
  const [adaptiveConfig, setAdaptiveConfig] = useState({
    enabled: true,
    adx_trending_threshold: 25,
    adx_ranging_threshold: 20,
    trending_indicators: ['MACD', 'Parabolic_SAR', 'EMA'],
    ranging_indicators: ['RSI', 'Stochastic', 'Bollinger_Bands'],
    trending_execution_delay: 1.0,
    ranging_execution_delay: 0.5,
    ranging_signal_threshold: 80
  });
  
  // Model Performance
  const [modelPerformance, setModelPerformance] = useState({});
  const [learningStats, setLearningStats] = useState({});
  
  // Retraining State
  const [isRetraining, setIsRetraining] = useState(false);
  const [retrainProgress, setRetrainProgress] = useState(0);
  const [retrainLog, setRetrainLog] = useState([]);
  
  // ML Training State
  const [mlModels, setMlModels] = useState([]);
  const [isTrainingML, setIsTrainingML] = useState(false);
  const [mlTrainingAsset, setMlTrainingAsset] = useState('EURUSD');
  const [mlTrainingTimeframe, setMlTrainingTimeframe] = useState('1h');
  const [mlTrainingDays, setMlTrainingDays] = useState(30);
  // Iter 87 — Train-from-Backtests scoping (asset + timeframe filters)
  const [mlBacktestAsset, setMlBacktestAsset] = useState('all');
  const [mlBacktestTimeframe, setMlBacktestTimeframe] = useState('5s');
  const [optimizationResults, setOptimizationResults] = useState(null);
  
  // Loading States
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Fetch data on mount
  useEffect(() => {
    fetchAllData();
  }, []);
  
  const fetchAllData = async () => {
    await Promise.all([
      fetchModelConfig(),
      fetchModelPerformance(),
      fetchLearningStats(),
      fetchAdaptiveConfig(),
      fetchMLModels()
    ]);
    setIsLoading(false);
  };
  
  const fetchMLModels = async () => {
    try {
      const response = await axios.get(`${API}/ml-training/models`);
      setMlModels(response.data.models || []);
    } catch (error) {
      console.error('Error fetching ML models:', error);
    }
  };
  
  const fetchModelConfig = async () => {
    try {
      const response = await axios.get(`${API}/ai-learning/config`);
      if (response.data) {
        setModelConfig(prev => ({ ...prev, ...response.data.model_config }));
        setLearningConfig(prev => ({ ...prev, ...response.data.learning_config }));
      }
    } catch (error) {
      console.error('Error fetching model config:', error);
    }
  };
  
  const fetchModelPerformance = async () => {
    try {
      const response = await axios.get(`${API}/ai-learning/performance`);
      setModelPerformance(response.data || {});
    } catch (error) {
      console.error('Error fetching model performance:', error);
    }
  };
  
  const fetchLearningStats = async () => {
    try {
      const response = await axios.get(`${API}/ai-learning/stats`);
      setLearningStats(response.data || {});
    } catch (error) {
      console.error('Error fetching learning stats:', error);
    }
  };
  
  const fetchAdaptiveConfig = async () => {
    try {
      const response = await axios.get(`${API}/adaptive-strategy/config`);
      if (response.data) {
        setAdaptiveConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching adaptive config:', error);
    }
  };
  
  // Save all settings
  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      // Save AI learning config
      await axios.post(`${API}/ai-learning/config`, {
        model_config: modelConfig,
        learning_config: learningConfig
      });
      
      // Save adaptive strategy config (uses PUT method)
      if (adaptiveConfig && Object.keys(adaptiveConfig).length > 0) {
        await axios.put(`${API}/adaptive-strategy/config`, adaptiveConfig);
      }
      
      toast.success('✅ AI/ML settings saved!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save settings: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSaving(false);
    }
  };
  
  // Retrain models
  const handleRetrainModels = async () => {
    setIsRetraining(true);
    setRetrainProgress(0);
    setRetrainLog([]);
    
    try {
      setRetrainLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: 'Clearing corrupted Win/Loss data...' }]);
      
      const response = await axios.post(`${API}/ml/clean-retrain`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to start retrain');
      }
      
      setRetrainLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: 'Clean retrain started — training all 4 ML models with fresh OANDA data...' }]);
      
      // Poll for status
      const pollInterval = setInterval(async () => {
        try {
          const status = await axios.get(`${API}/ml/retrain-status`);
          const d = status.data;
          
          setRetrainProgress(d.progress || 0);
          
          if (d.phase && d.phase !== 'starting') {
            const phaseNames = {
              clearing_corrupted_data: 'Clearing corrupted performance data...',
              training_maximized_ml_v3: 'Training Maximized ML v3.0 (XGBoost/LightGBM)...',
              training_improved_ml_v2: 'Training Improved ML v2.0 (RF/GB/AdaBoost)...',
              training_lstm_gru: 'Training LSTM/GRU time-series model...',
              training_ppo_rl: 'Training PPO Reinforcement Learning agent...',
              complete: 'All models retrained successfully!',
              error: `Error: ${d.error}`
            };
            const msg = phaseNames[d.phase] || d.phase;
            setRetrainLog(prev => {
              if (prev.length === 0 || prev[prev.length - 1].msg !== msg) {
                return [...prev, { time: new Date().toLocaleTimeString(), msg }];
              }
              return prev;
            });
          }
          
          if (!d.running) {
            clearInterval(pollInterval);
            setRetrainProgress(100);
            
            // Log results
            if (d.results) {
              Object.entries(d.results).forEach(([key, val]) => {
                if (key === 'cleared_collections') return;
                const status = val.success ? 'trained' : val.skipped ? 'skipped' : 'error';
                setRetrainLog(prev => [...prev, { 
                  time: new Date().toLocaleTimeString(), 
                  msg: `${status === 'trained' ? 'OK' : status === 'skipped' ? 'SKIP' : 'ERR'} ${key}: ${val.accuracy ? val.accuracy + '%' : val.skipped || val.error || 'done'}`
                }]);
              });
            }
            
            if (d.phase === 'complete') {
              toast.success('All ML models retrained with fresh data!');
            } else {
              toast.error(`Retrain ended: ${d.error || 'unknown error'}`);
            }
            setIsRetraining(false);
            fetchModelPerformance();
          }
        } catch (err) {
          // polling error, keep trying
        }
      }, 3000);
      
    } catch (error) {
      setRetrainLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: `Error: ${error.message}` }]);
      toast.error('Failed to start clean retrain');
      setIsRetraining(false);
    }
  };
  
  // Reset learning
  const handleResetLearning = async () => {
    if (!window.confirm('Are you sure you want to reset all learned parameters? This cannot be undone.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/ai-learning/reset`);
      toast.success('Learning parameters reset');
      fetchLearningStats();
    } catch (error) {
      toast.error('Failed to reset learning');
    }
  };

  // Train ML models from backtest results
  const handleTrainFromBacktests = async () => {
    setIsTrainingML(true);
    try {
      toast.info(`Training ML models from backtests — asset=${mlBacktestAsset}, tf=${mlBacktestTimeframe}...`);
      const response = await axios.post(`${API}/ml-training/train-from-backtests`, {
        limit: 200,
        asset: mlBacktestAsset,
        timeframe: mlBacktestTimeframe,
      });
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        fetchMLModels();
      } else {
        toast.error(response.data.error || 'Training failed');
      }
    } catch (error) {
      toast.error('Failed to train ML models');
    } finally {
      setIsTrainingML(false);
    }
  };

  // Train ML on price data
  const handleTrainOnPriceData = async () => {
    setIsTrainingML(true);
    try {
      toast.info(`Training ML models on ${mlTrainingAsset} ${mlTrainingTimeframe}...`);
      const response = await axios.post(`${API}/ml-training/train-on-price-data`, {
        asset: mlTrainingAsset,
        timeframe: mlTrainingTimeframe,
        days: mlTrainingDays
      });
      
      if (response.data.success) {
        toast.success(`✅ Trained on ${response.data.candles_used} candles`);
        fetchMLModels();
      } else {
        toast.error(response.data.error || 'Training failed');
      }
    } catch (error) {
      toast.error('Failed to train on price data');
    } finally {
      setIsTrainingML(false);
    }
  };

  // Run strategy optimization
  const handleRunOptimization = async () => {
    try {
      toast.info('Running strategy optimization...');
      const response = await axios.post(`${API}/ml-training/run-optimization`);
      
      if (response.data.success) {
        setOptimizationResults(response.data);
        toast.success('✅ Optimization complete!');
      } else {
        toast.error(response.data.error || 'Optimization failed');
      }
    } catch (error) {
      toast.error('Failed to run optimization');
    }
  };

  // Iter 60 — Reset optimization history (wipe contaminated backtest_results)
  const handleResetOptimizationHistory = async (mode = "negative_only") => {
    const isFullWipe = mode === "all";
    const msg = isFullWipe
      ? "Wipe ALL backtest results? This deletes profitable rows too. (Type DELETE to confirm)"
      : "Wipe ONLY money-losing rows (likely contaminated from pre-Iter-60 noise + synthetic-data bugs)?\n\nProfitable backtests will be preserved. Continue?";
    const ok = window.confirm(msg);
    if (!ok) return;
    if (isFullWipe) {
      const typed = window.prompt('Type DELETE to confirm full wipe:');
      if (typed !== 'DELETE') {
        toast.info('Reset cancelled.');
        return;
      }
    }
    try {
      const response = await axios.post(`${API}/ml-training/reset-optimization-history`, {
        confirm: "yes",
        only_negative: !isFullWipe,
      });
      if (response.data.success) {
        toast.success(
          `🧹 Deleted ${response.data.deleted} backtest rows. ${response.data.remaining} remaining.`
        );
        // Refresh optimization results so the UI reflects the cleanup
        setOptimizationResults(null);
      } else {
        toast.error(response.data.error || 'Reset failed');
      }
    } catch (error) {
      toast.error('Failed to reset optimization history: ' + (error?.message || ''));
    }
  };

  // Schedule daily retraining
  const handleScheduleDailyRetrain = async () => {
    try {
      const response = await axios.post(`${API}/ml-training/schedule-daily-retrain`);
      if (response.data.success) {
        toast.success('✅ Daily retraining scheduled');
      }
    } catch (error) {
      toast.error('Failed to schedule retraining');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Brain className="w-8 h-8 text-purple-400" />
            AI/ML Models
          </h1>
          <p className="text-slate-400 mt-1">Configure AI learning systems and trading models</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={learningConfig.enabled ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}>
            {learningConfig.enabled ? 'Learning Active' : 'Learning Paused'}
          </Badge>
          <Button onClick={handleSaveSettings} disabled={isSaving} className="bg-purple-500 hover:bg-purple-600">
            {isSaving ? 'Saving...' : 'Save All Settings'}
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-dark border-slate-700/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Primary Model</p>
                <p className="text-lg font-bold text-white truncate">{modelConfig.primary_model.replace(/_/g, ' ')}</p>
              </div>
              <Cpu className="w-6 h-6 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-dark border-slate-700/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Avg Model Accuracy</p>
                <p className="text-lg font-bold text-green-400">{modelPerformance.average_accuracy?.toFixed(1) || 75}%</p>
              </div>
              <Target className="w-6 h-6 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-dark border-slate-700/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Learning Cycles</p>
                <p className="text-lg font-bold text-white">{learningStats.total_cycles || 0}</p>
              </div>
              <RotateCcw className="w-6 h-6 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-dark border-slate-700/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Last Retrain</p>
                <p className="text-lg font-bold text-white">{learningStats.last_retrain || 'Never'}</p>
              </div>
              <Clock className="w-6 h-6 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="real-data" className="space-y-4">
        <TabsList className="bg-slate-800/50 flex-wrap">
          <TabsTrigger value="real-data">📊 Real Data Training</TabsTrigger>
          <TabsTrigger value="models">🧠 Model Selection</TabsTrigger>
          <TabsTrigger value="ml-training">🤖 ML Training</TabsTrigger>
          <TabsTrigger value="optimization">🎯 Optimization</TabsTrigger>
          <TabsTrigger value="learning">📚 Learning System</TabsTrigger>
          <TabsTrigger value="adaptive">🔄 Adaptive Strategy</TabsTrigger>
          <TabsTrigger value="retrain">Retrain Models</TabsTrigger>
          <TabsTrigger value="otc-tuning">5s OTC Tuning</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        {/* Real Data Training Tab - NEW */}
        <TabsContent value="real-data" className="space-y-4">
          <DataCollectionDashboard />
        </TabsContent>

        {/* Model Selection Tab */}
        <TabsContent value="models" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Model Selection */}
            <Card className="glass-dark border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white">Signal Generation Models</CardTitle>
                <CardDescription>Select primary and secondary models for signal generation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Primary Model</Label>
                  <Select 
                    value={modelConfig.primary_model}
                    onValueChange={(v) => setModelConfig(prev => ({ ...prev, primary_model: v }))}
                  >
                    <SelectTrigger className="bg-slate-800/50 border-slate-600 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {AVAILABLE_MODELS.map(model => (
                        <SelectItem key={model.id} value={model.id}>
                          <span className="flex items-center gap-2">
                            {model.name}
                            <Badge variant="outline" className="text-xs">{model.accuracy}%</Badge>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Secondary Model (Confirmation)</Label>
                  <Select 
                    value={modelConfig.secondary_model}
                    onValueChange={(v) => setModelConfig(prev => ({ ...prev, secondary_model: v }))}
                  >
                    <SelectTrigger className="bg-slate-800/50 border-slate-600 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {AVAILABLE_MODELS.map(model => (
                        <SelectItem key={model.id} value={model.id}>
                          <span className="flex items-center gap-2">
                            {model.name}
                            <Badge variant="outline" className="text-xs">{model.accuracy}%</Badge>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center justify-between pt-4">
                  <div>
                    <Label>Use Ensemble (Combine Models)</Label>
                    <p className="text-xs text-slate-500">Combine multiple models for better accuracy</p>
                  </div>
                  <Switch
                    checked={modelConfig.use_ensemble}
                    onCheckedChange={(v) => setModelConfig(prev => ({ ...prev, use_ensemble: v }))}
                  />
                </div>
                
                {modelConfig.use_ensemble && (
                  <div>
                    <Label>Ensemble Method</Label>
                    <Select 
                      value={modelConfig.ensemble_method}
                      onValueChange={(v) => setModelConfig(prev => ({ ...prev, ensemble_method: v }))}
                    >
                      <SelectTrigger className="bg-slate-800/50 border-slate-600 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        <SelectItem value="majority_vote">Majority Vote</SelectItem>
                        <SelectItem value="weighted_average">Weighted Average</SelectItem>
                        <SelectItem value="stacking">Stacking</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Available Models List */}
            <Card className="glass-dark border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white">Available Models</CardTitle>
                <CardDescription>All trading models and their baseline accuracy</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {AVAILABLE_MODELS.map(model => (
                    <div 
                      key={model.id}
                      className={`p-3 rounded-lg border ${
                        model.id === modelConfig.primary_model 
                          ? 'border-purple-500 bg-purple-500/10' 
                          : model.id === modelConfig.secondary_model
                            ? 'border-blue-500 bg-blue-500/10'
                            : 'border-slate-700 bg-slate-800/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">{model.name}</p>
                          <p className="text-xs text-slate-400">{model.category}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-slate-700">{model.accuracy}%</Badge>
                          {model.id === modelConfig.primary_model && (
                            <Badge className="bg-purple-500/20 text-purple-400">Primary</Badge>
                          )}
                          {model.id === modelConfig.secondary_model && (
                            <Badge className="bg-blue-500/20 text-blue-400">Secondary</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ML Training Tab */}
        <TabsContent value="ml-training" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Train from Backtest Results */}
            <Card className="glass-dark border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Train from Backtest Results
                </CardTitle>
                <CardDescription>Use backtest trade outcomes to train ML classifiers</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-blue-500/10 border-blue-500/30">
                  <Info className="w-4 h-4 text-blue-400" />
                  <AlertDescription className="text-blue-300 text-sm">
                    ML models learn from your backtest history to identify winning patterns. 
                    More backtests = better training.
                  </AlertDescription>
                </Alert>

                {/* Iter 87 — Asset + Timeframe scoping */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-sm">Asset</Label>
                    <Select value={mlBacktestAsset} onValueChange={setMlBacktestAsset}>
                      <SelectTrigger
                        className="bg-slate-800/50 border-slate-600 mt-1"
                        data-testid="train-from-backtests-asset"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600 max-h-72 overflow-y-auto">
                        <SelectItem value="all">All assets</SelectItem>
                        <SelectItem value="EURUSD_OTC">EUR/USD (OTC)</SelectItem>
                        <SelectItem value="GBPUSD_OTC">GBP/USD (OTC)</SelectItem>
                        <SelectItem value="USDJPY_OTC">USD/JPY (OTC)</SelectItem>
                        <SelectItem value="AUDUSD_OTC">AUD/USD (OTC)</SelectItem>
                        <SelectItem value="USDCAD_OTC">USD/CAD (OTC)</SelectItem>
                        <SelectItem value="USDCHF_OTC">USD/CHF (OTC)</SelectItem>
                        <SelectItem value="NZDUSD_OTC">NZD/USD (OTC)</SelectItem>
                        <SelectItem value="EURGBP_OTC">EUR/GBP (OTC)</SelectItem>
                        <SelectItem value="EURJPY_OTC">EUR/JPY (OTC)</SelectItem>
                        <SelectItem value="GBPJPY_OTC">GBP/JPY (OTC)</SelectItem>
                        <SelectItem value="EURUSD">EUR/USD</SelectItem>
                        <SelectItem value="GBPUSD">GBP/USD</SelectItem>
                        <SelectItem value="USDJPY">USD/JPY</SelectItem>
                        <SelectItem value="BTCUSD_OTC">BTC/USD (OTC)</SelectItem>
                        <SelectItem value="ETHUSD_OTC">ETH/USD (OTC)</SelectItem>
                        <SelectItem value="XAUUSD_OTC">Gold/XAU (OTC)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Timeframe</Label>
                    <Select value={mlBacktestTimeframe} onValueChange={setMlBacktestTimeframe}>
                      <SelectTrigger
                        className="bg-slate-800/50 border-slate-600 mt-1"
                        data-testid="train-from-backtests-timeframe"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        <SelectItem value="5s">5 Seconds</SelectItem>
                        <SelectItem value="10s">10 Seconds</SelectItem>
                        <SelectItem value="15s">15 Seconds</SelectItem>
                        <SelectItem value="30s">30 Seconds</SelectItem>
                        <SelectItem value="1m">1 Minute</SelectItem>
                        <SelectItem value="5m">5 Minutes</SelectItem>
                        <SelectItem value="15m">15 Minutes</SelectItem>
                        <SelectItem value="1h">1 Hour</SelectItem>
                        <SelectItem value="all">All timeframes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-slate-400 text-sm">Models trained:</p>
                  {ML_MODEL_TYPES.map(model => (
                    <div key={model.id} className="flex items-center gap-3 p-2 bg-slate-800/30 rounded-lg">
                      <span className="text-xl">{model.icon}</span>
                      <div>
                        <p className="text-white font-medium">{model.name}</p>
                        <p className="text-xs text-slate-400">{model.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <Button 
                  onClick={handleTrainFromBacktests}
                  disabled={isTrainingML}
                  className="w-full bg-purple-500 hover:bg-purple-600"
                  data-testid="train-from-backtests-btn"
                >
                  {isTrainingML ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Training...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Train from Backtests
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Train on Price Data */}
            <Card className="glass-dark border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <LineChart className="w-5 h-5" />
                  Train on Price Data
                </CardTitle>
                <CardDescription>Train ML models on historical price data for specific assets</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300 text-sm">Asset</Label>
                    <Select value={mlTrainingAsset} onValueChange={setMlTrainingAsset}>
                      <SelectTrigger className="bg-slate-800/50 border-slate-600 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600 max-h-60 overflow-y-auto">
                        {/* Forex OTC */}
                        <SelectItem value="EURUSD_otc">EUR/USD (OTC)</SelectItem>
                        <SelectItem value="GBPUSD_otc">GBP/USD (OTC)</SelectItem>
                        <SelectItem value="USDJPY_otc">USD/JPY (OTC)</SelectItem>
                        <SelectItem value="AUDUSD_otc">AUD/USD (OTC)</SelectItem>
                        <SelectItem value="USDCAD_otc">USD/CAD (OTC)</SelectItem>
                        <SelectItem value="USDCHF_otc">USD/CHF (OTC)</SelectItem>
                        <SelectItem value="NZDUSD_otc">NZD/USD (OTC)</SelectItem>
                        <SelectItem value="EURGBP_otc">EUR/GBP (OTC)</SelectItem>
                        <SelectItem value="EURJPY_otc">EUR/JPY (OTC)</SelectItem>
                        <SelectItem value="GBPJPY_otc">GBP/JPY (OTC)</SelectItem>
                        <SelectItem value="AUDCAD_otc">AUD/CAD (OTC)</SelectItem>
                        <SelectItem value="AUDJPY_otc">AUD/JPY (OTC)</SelectItem>
                        <SelectItem value="CADJPY_otc">CAD/JPY (OTC)</SelectItem>
                        <SelectItem value="EURAUD_otc">EUR/AUD (OTC)</SelectItem>
                        <SelectItem value="EURCAD_otc">EUR/CAD (OTC)</SelectItem>
                        <SelectItem value="GBPAUD_otc">GBP/AUD (OTC)</SelectItem>
                        <SelectItem value="GBPCAD_otc">GBP/CAD (OTC)</SelectItem>
                        {/* Forex Regular */}
                        <SelectItem value="EURUSD">EUR/USD</SelectItem>
                        <SelectItem value="GBPUSD">GBP/USD</SelectItem>
                        <SelectItem value="USDJPY">USD/JPY</SelectItem>
                        <SelectItem value="AUDUSD">AUD/USD</SelectItem>
                        <SelectItem value="USDCAD">USD/CAD</SelectItem>
                        <SelectItem value="NZDUSD">NZD/USD</SelectItem>
                        <SelectItem value="EURGBP">EUR/GBP</SelectItem>
                        <SelectItem value="EURJPY">EUR/JPY</SelectItem>
                        <SelectItem value="GBPJPY">GBP/JPY</SelectItem>
                        {/* Crypto OTC */}
                        <SelectItem value="BTCUSD_otc">BTC/USD (OTC)</SelectItem>
                        <SelectItem value="ETHUSD_otc">ETH/USD (OTC)</SelectItem>
                        <SelectItem value="LTCUSD_otc">LTC/USD (OTC)</SelectItem>
                        <SelectItem value="XRPUSD_otc">XRP/USD (OTC)</SelectItem>
                        <SelectItem value="BNBUSD_otc">BNB/USD (OTC)</SelectItem>
                        <SelectItem value="ADAUSD_otc">ADA/USD (OTC)</SelectItem>
                        <SelectItem value="SOLUSD_otc">SOL/USD (OTC)</SelectItem>
                        {/* Crypto Regular */}
                        <SelectItem value="BTCUSD">BTC/USD</SelectItem>
                        <SelectItem value="ETHUSD">ETH/USD</SelectItem>
                        <SelectItem value="LTCUSD">LTC/USD</SelectItem>
                        {/* Indices OTC */}
                        <SelectItem value="US100_otc">US100/NASDAQ (OTC)</SelectItem>
                        <SelectItem value="US500_otc">US500/S&P500 (OTC)</SelectItem>
                        <SelectItem value="US30_otc">US30/Dow Jones (OTC)</SelectItem>
                        <SelectItem value="DE30_otc">DE30/DAX (OTC)</SelectItem>
                        <SelectItem value="UK100_otc">UK100/FTSE (OTC)</SelectItem>
                        {/* Commodities OTC */}
                        <SelectItem value="XAUUSD_otc">Gold/XAU (OTC)</SelectItem>
                        <SelectItem value="XAGUSD_otc">Silver/XAG (OTC)</SelectItem>
                        <SelectItem value="WTIUSD_otc">Oil/WTI (OTC)</SelectItem>
                        {/* Commodities Regular */}
                        <SelectItem value="XAUUSD">Gold/XAU</SelectItem>
                        <SelectItem value="XAGUSD">Silver/XAG</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Timeframe</Label>
                    <Select value={mlTrainingTimeframe} onValueChange={setMlTrainingTimeframe}>
                      <SelectTrigger className="bg-slate-800/50 border-slate-600 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        <SelectItem value="5s">5 Seconds</SelectItem>
                        <SelectItem value="10s">10 Seconds</SelectItem>
                        <SelectItem value="15s">15 Seconds</SelectItem>
                        <SelectItem value="30s">30 Seconds</SelectItem>
                        <SelectItem value="1m">1 Minute</SelectItem>
                        <SelectItem value="5m">5 Minutes</SelectItem>
                        <SelectItem value="15m">15 Minutes</SelectItem>
                        <SelectItem value="1h">1 Hour</SelectItem>
                        <SelectItem value="4h">4 Hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-300 text-sm">Training Period: {mlTrainingDays} days</Label>
                  <Slider
                    value={[mlTrainingDays]}
                    onValueChange={([v]) => setMlTrainingDays(v)}
                    min={7}
                    max={90}
                    className="mt-2"
                  />
                </div>
                
                <Button 
                  onClick={handleTrainOnPriceData}
                  disabled={isTrainingML}
                  className="w-full bg-green-500 hover:bg-green-600"
                >
                  {isTrainingML ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Training...
                    </>
                  ) : (
                    <>
                      <Brain className="w-4 h-4 mr-2" />
                      Train on Price Data
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Trained ML Models */}
          <Card className="glass-dark border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Cpu className="w-5 h-5" />
                Trained ML Models ({mlModels.length})
              </CardTitle>
              <CardDescription>Models ready for signal generation</CardDescription>
            </CardHeader>
            <CardContent>
              {mlModels.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No ML models trained yet</p>
                  <p className="text-sm">Train models from backtests or price data above</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {mlModels.map((model, idx) => (
                    <div key={idx} className="p-4 bg-slate-800/30 rounded-lg border border-slate-700">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-white">{model.name || model.model_type}</span>
                        <Badge className={model.is_active ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}>
                          {model.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Accuracy</span>
                          <span className="text-green-400">{(model.metrics?.accuracy * 100 || 0).toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">F1 Score</span>
                          <span className="text-purple-400">{(model.metrics?.f1_score * 100 || 0).toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Training Samples</span>
                          <span className="text-white">{model.metrics?.training_samples || 0}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Schedule Daily Retraining */}
          <Card className="glass-dark border-purple-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-400" />
                    Continuous Learning
                  </h3>
                  <p className="text-slate-400 text-sm mt-1">
                    Schedule daily automatic model retraining at midnight UTC
                  </p>
                </div>
                <Button onClick={handleScheduleDailyRetrain} className="bg-purple-500 hover:bg-purple-600">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Enable Daily Retrain
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Optimization Tab */}
        <TabsContent value="optimization" className="space-y-6">
          <Card className="glass-dark border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Target className="w-5 h-5" />
                Strategy Optimization
              </CardTitle>
              <CardDescription>ML-based analysis to find optimal strategy parameters</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-purple-500/10 border-purple-500/30">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <AlertDescription className="text-purple-300 text-sm">
                  Optimization analyzes all your backtest results to identify the best performing 
                  strategies and provides recommendations for live trading.
                </AlertDescription>
              </Alert>
              
              <div className="flex flex-wrap gap-2" data-testid="optimization-actions">
                <Button
                  onClick={handleRunOptimization}
                  className="bg-purple-500 hover:bg-purple-600"
                  data-testid="run-optimization-btn"
                >
                  <Award className="w-4 h-4 mr-2" />
                  Run Strategy Optimization
                </Button>
                <Button
                  onClick={() => handleResetOptimizationHistory("negative_only")}
                  variant="outline"
                  className="border-amber-600 text-amber-300 hover:bg-amber-900/30"
                  data-testid="reset-loss-history-btn"
                  title="Wipe only loss-making rows (the pre-Iter-60 contaminated ones). Profitable backtests stay intact."
                >
                  🧹 Reset Loss-Maker History
                </Button>
                <Button
                  onClick={() => handleResetOptimizationHistory("all")}
                  variant="outline"
                  className="border-red-600 text-red-300 hover:bg-red-900/30"
                  data-testid="reset-all-history-btn"
                  title="Nuclear option: wipe ALL backtest results (profitable + losing)."
                >
                  ⚠ Wipe ALL History
                </Button>
              </div>
              
              {optimizationResults && (
                <div className="space-y-4 mt-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-800/30 rounded-lg text-center">
                      <p className="text-slate-400 text-sm">Results Analyzed</p>
                      <p className="text-2xl font-bold text-white">{optimizationResults.total_results_analyzed}</p>
                    </div>
                    <div className="p-4 bg-slate-800/30 rounded-lg text-center">
                      <p className="text-slate-400 text-sm">Strategies Compared</p>
                      <p className="text-2xl font-bold text-white">{optimizationResults.strategies_analyzed}</p>
                    </div>
                    <div className="p-4 bg-slate-800/30 rounded-lg text-center">
                      <p className="text-slate-400 text-sm">Best Strategy</p>
                      <p className="text-lg font-bold text-purple-400">{optimizationResults.best_strategy?.strategy?.replace(/_/g, ' ')}</p>
                    </div>
                    <div className="p-4 bg-slate-800/30 rounded-lg text-center">
                      <p className="text-slate-400 text-sm">Best Win Rate</p>
                      <p className="text-2xl font-bold text-green-400">{optimizationResults.best_strategy?.avg_win_rate}%</p>
                    </div>
                  </div>
                  
                  {/* Recommendations */}
                  <div>
                    <h4 className="text-white font-medium mb-3">Strategy Rankings</h4>
                    <div className="space-y-2">
                      {optimizationResults.all_recommendations?.map((rec, idx) => (
                        <div key={idx} className={`p-3 rounded-lg border ${
                          rec.recommendation === 'HIGH' ? 'border-green-500/30 bg-green-500/10' :
                          rec.recommendation === 'MEDIUM' ? 'border-yellow-500/30 bg-yellow-500/10' :
                          'border-red-500/30 bg-red-500/10'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">{rec.strategy?.replace(/_/g, ' ')}</p>
                              <p className="text-xs text-slate-400">{rec.total_trades} trades analyzed</p>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${
                                rec.avg_win_rate >= 55 ? 'text-green-400' : 
                                rec.avg_win_rate >= 45 ? 'text-yellow-400' : 'text-red-400'
                              }`}>{rec.avg_win_rate}% Win Rate</p>
                              <p className={rec.avg_roi >= 0 ? 'text-green-400' : 'text-red-400'}>
                                {rec.avg_roi >= 0 ? '+' : ''}{rec.avg_roi}% ROI
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Tips */}
                  <div className="p-4 bg-slate-800/30 rounded-lg">
                    <h4 className="text-white font-medium mb-2">💡 Optimization Tips</h4>
                    <ul className="space-y-1 text-sm text-slate-400">
                      {optimizationResults.optimization_tips?.map((tip, idx) => (
                        <li key={idx}>• {tip}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Learning System Tab */}
        <TabsContent value="learning">
          <Card className="glass-dark border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Database className="w-5 h-5" />
                AI Learning System Configuration
              </CardTitle>
              <CardDescription>Configure how the AI learns and adapts to market conditions</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
                <div>
                  <Label className="text-lg">Enable AI Learning</Label>
                  <p className="text-sm text-slate-400">Allow the system to learn from trade outcomes</p>
                </div>
                <Switch
                  checked={learningConfig.enabled}
                  onCheckedChange={(v) => setLearningConfig(prev => ({ ...prev, enabled: v }))}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Learning Rate: {learningConfig.learning_rate}</Label>
                  <Slider
                    value={[learningConfig.learning_rate * 100]}
                    onValueChange={([v]) => setLearningConfig(prev => ({ ...prev, learning_rate: v / 100 }))}
                    min={1}
                    max={10}
                    className="mt-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">Higher = faster adaptation, but less stable</p>
                </div>
                
                <div>
                  <Label>Adaptation Speed</Label>
                  <Select 
                    value={learningConfig.adaptation_speed}
                    onValueChange={(v) => setLearningConfig(prev => ({ ...prev, adaptation_speed: v }))}
                  >
                    <SelectTrigger className="bg-slate-800/50 border-slate-600 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      <SelectItem value="slow">Slow (Conservative)</SelectItem>
                      <SelectItem value="medium">Medium (Balanced)</SelectItem>
                      <SelectItem value="fast">Fast (Aggressive)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Lookback Periods: {learningConfig.lookback_periods}</Label>
                  <Slider
                    value={[learningConfig.lookback_periods]}
                    onValueChange={([v]) => setLearningConfig(prev => ({ ...prev, lookback_periods: v }))}
                    min={20}
                    max={500}
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label>Min Samples for Update: {learningConfig.min_samples_for_update}</Label>
                  <Slider
                    value={[learningConfig.min_samples_for_update]}
                    onValueChange={([v]) => setLearningConfig(prev => ({ ...prev, min_samples_for_update: v }))}
                    min={10}
                    max={200}
                    className="mt-2"
                  />
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 pt-4 border-t border-slate-700">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={learningConfig.use_market_regime}
                    onCheckedChange={(v) => setLearningConfig(prev => ({ ...prev, use_market_regime: v }))}
                  />
                  <Label>Market Regime Detection</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={learningConfig.use_volatility_filter}
                    onCheckedChange={(v) => setLearningConfig(prev => ({ ...prev, use_volatility_filter: v }))}
                  />
                  <Label>Volatility Filter</Label>
                </div>
              </div>
              
              <Alert className="bg-blue-500/10 border-blue-500/30">
                <Info className="w-4 h-4 text-blue-400" />
                <AlertDescription className="text-blue-300 text-sm">
                  The AI learning system continuously analyzes trade outcomes to improve signal accuracy over time.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Adaptive Strategy Tab */}
        <TabsContent value="adaptive">
          <Card className="glass-dark border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Adaptive Strategy Configuration
              </CardTitle>
              <CardDescription>Automatically switch strategies based on performance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
                <div>
                  <Label className="text-lg">Enable Adaptive Strategy</Label>
                  <p className="text-sm text-slate-400">Auto-adapt to trending vs ranging markets</p>
                </div>
                <Switch
                  checked={adaptiveConfig.enabled}
                  onCheckedChange={(v) => setAdaptiveConfig(prev => ({ ...prev, enabled: v }))}
                />
              </div>
              
              {adaptiveConfig.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label>ADX Trending Threshold: {adaptiveConfig.adx_trending_threshold}</Label>
                    <p className="text-xs text-slate-500 mb-2">Higher = stronger trend required</p>
                    <Slider
                      value={[adaptiveConfig.adx_trending_threshold]}
                      onValueChange={([v]) => setAdaptiveConfig(prev => ({ ...prev, adx_trending_threshold: v }))}
                      min={15}
                      max={50}
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label>ADX Ranging Threshold: {adaptiveConfig.adx_ranging_threshold}</Label>
                    <p className="text-xs text-slate-500 mb-2">Lower = confirms range-bound market</p>
                    <Slider
                      value={[adaptiveConfig.adx_ranging_threshold]}
                      onValueChange={([v]) => setAdaptiveConfig(prev => ({ ...prev, adx_ranging_threshold: v }))}
                      min={10}
                      max={30}
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label>Trending Execution Delay: {adaptiveConfig.trending_execution_delay}s</Label>
                    <p className="text-xs text-slate-500 mb-2">Wait time before trending trades</p>
                    <Slider
                      value={[adaptiveConfig.trending_execution_delay * 10]}
                      onValueChange={([v]) => setAdaptiveConfig(prev => ({ ...prev, trending_execution_delay: v / 10 }))}
                      min={5}
                      max={100}
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label>Ranging Signal Threshold: {adaptiveConfig.ranging_signal_threshold}%</Label>
                    <p className="text-xs text-slate-500 mb-2">Min confidence for range trades</p>
                    <Slider
                      value={[adaptiveConfig.ranging_signal_threshold]}
                      onValueChange={([v]) => setAdaptiveConfig(prev => ({ ...prev, ranging_signal_threshold: v }))}
                      min={70}
                      max={95}
                      className="mt-2"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Retrain Models Tab */}
        <TabsContent value="retrain">
          <Card className="glass-dark border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Clean Retrain All Models
              </CardTitle>
              <CardDescription>Clear corrupted data and retrain all ML models with fresh OANDA market data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert className="bg-amber-500/10 border-amber-500/30">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <AlertDescription className="text-amber-300 text-sm">
                  <strong>Why retrain?</strong> Previous Win/Loss detection had a bug that corrupted performance tracking data. 
                  Clean retrain clears all corrupted data and retrains 4 ML models (Maximized v3, Improved v2, LSTM/GRU, PPO RL) 
                  using fresh OANDA price data. This takes 2-5 minutes.
                </AlertDescription>
              </Alert>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50 text-center">
                  <div className="text-xs text-slate-400 mb-1">Maximized v3</div>
                  <div className="text-sm font-semibold text-purple-400">XGBoost + LightGBM</div>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50 text-center">
                  <div className="text-xs text-slate-400 mb-1">Improved v2</div>
                  <div className="text-sm font-semibold text-blue-400">RF + GB + AdaBoost</div>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50 text-center">
                  <div className="text-xs text-slate-400 mb-1">LSTM/GRU</div>
                  <div className="text-sm font-semibold text-emerald-400">Time-Series</div>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50 text-center">
                  <div className="text-xs text-slate-400 mb-1">PPO RL</div>
                  <div className="text-sm font-semibold text-orange-400">Reinforcement</div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Button
                  data-testid="clean-retrain-btn"
                  onClick={handleRetrainModels}
                  disabled={isRetraining}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold"
                >
                  {isRetraining ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Retraining...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Clean Retrain All Models
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={handleResetLearning}
                  variant="outline"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset Learning Data
                </Button>
              </div>
              
              {isRetraining && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Progress</span>
                    <span className="text-white">{Math.round(retrainProgress)}%</span>
                  </div>
                  <Progress value={retrainProgress} className="h-2" />
                </div>
              )}
              
              {retrainLog.length > 0 && (
                <div className="bg-slate-900 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <p className="text-sm text-slate-400 mb-2">Training Log:</p>
                  {retrainLog.map((log, idx) => (
                    <p key={idx} className="text-xs text-slate-300 font-mono">
                      [{log.time}] {log.msg}
                    </p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* OTC Tuning Tab */}
        <TabsContent value="otc-tuning">
          <OTCTuningPanel />
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance">
          <Card className="glass-dark border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Model Performance
              </CardTitle>
              <CardDescription>Track accuracy and performance of each model</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {AVAILABLE_MODELS.map(model => {
                  const perf = modelPerformance[model.id] || { accuracy: model.accuracy, trades: 0 };
                  return (
                    <div key={model.id} className="p-4 bg-slate-800/30 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">{model.name}</span>
                          {model.id === modelConfig.primary_model && (
                            <Badge className="bg-purple-500/20 text-purple-400 text-xs">Primary</Badge>
                          )}
                        </div>
                        <span className={`font-bold ${perf.accuracy >= 70 ? 'text-green-400' : 'text-yellow-400'}`}>
                          {perf.accuracy?.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Progress value={perf.accuracy} className="h-2 flex-1" />
                        <span className="text-xs text-slate-500">{perf.trades || 0} trades</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIMLModelsPage;
